import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";

import * as client from "braintree-web/client";
import * as dataCollector from "braintree-web/data-collector";
import * as usBankAccount from "braintree-web/us-bank-account";
import { Client } from "braintree-web/client";
import { DataCollector } from "braintree-web/data-collector";
import { USBankAccount } from "braintree-web/us-bank-account";

import { RepositoryBraintreeService } from "./repository-braintree.service";

export const BankAccountType = {
  Checking: "checking",
  Savings: "savings",
} as const;

export type BankAccountTypeValue =
  (typeof BankAccountType)[keyof typeof BankAccountType];

export const BankOwnershipType = {
  Personal: "personal",
  Business: "business",
} as const;

export type BankOwnershipTypeValue =
  (typeof BankOwnershipType)[keyof typeof BankOwnershipType];

export interface BankBillingAddress {
  streetAddress: string;
  extendedAddress?: string;
  locality: string;
  region: string;
  postalCode: string;
}

export interface BankAccountDetails {
  routingNumber: string;
  accountNumber: string;
  accountType: BankAccountTypeValue;
  billingAddress: BankBillingAddress;
  ownershipType: BankOwnershipTypeValue;
  firstName?: string;
  lastName?: string;
  businessName?: string;
}

export interface BankAccountTokenizePayload {
  nonce: string;
  type?: string;
  details?: Record<string, unknown>;
}

/**
 * eCheck runs on its own Braintree client so tearing down the card and wallet
 * instances never invalidates an in-flight bank tokenization.
 */
@Injectable({
  providedIn: "root",
})
export class EcheckSdkService {
  private _braintree = inject(RepositoryBraintreeService);

  private _client: Client | null = null;
  private _dataCollector: DataCollector | null = null;
  private _usBankAccount: USBankAccount | null = null;
  private _destroyed = false;

  /** Serializes initialize/teardown so an older teardown cannot clear a newer client. */
  private _lifecycle: Promise<unknown> = Promise.resolve();

  get isReady(): boolean {
    return !!this._usBankAccount;
  }

  initialize(): Promise<void> {
    return this._enqueue(async () => {
      this._destroyed = false;
      if (this._usBankAccount) {
        return;
      }
      await this._initialize();
    });
  }

  async tokenizeBankAccount(
    details: BankAccountDetails,
    mandateText: string
  ): Promise<BankAccountTokenizePayload> {
    if (!this._usBankAccount) {
      await this.initialize();
    }

    if (!this._usBankAccount) {
      throw new Error("The bank account form is not ready.");
    }

    return new Promise((resolve, reject) => {
      this._usBankAccount.tokenize(
        {
          bankDetails: details,
          bankLogin: null,
          mandateText,
        },
        (error, payload) => {
          if (error) {
            reject(error);
            return;
          }

          resolve(payload as BankAccountTokenizePayload);
        }
      );
    });
  }

  teardown(): Promise<void> {
    return this._enqueue(() => this._teardownInstances());
  }

  private _enqueue<T>(task: () => Promise<T>): Promise<T> {
    const run = this._lifecycle.then(task, task);
    this._lifecycle = run.then(
      () => undefined,
      () => undefined
    );
    return run;
  }

  private async _teardownInstances(): Promise<void> {
    this._destroyed = true;

    const bank = this._usBankAccount;
    const collector = this._dataCollector;
    const instance = this._client;

    await Promise.all([
      this._safeTeardown(bank),
      this._safeTeardown(collector),
    ]);

    if (this._usBankAccount === bank) {
      this._usBankAccount = null;
    }
    if (this._dataCollector === collector) {
      this._dataCollector = null;
    }
    if (this._client === instance) {
      this._client = null;
    }
  }

  private async _initialize(): Promise<void> {
    const clientToken = await firstValueFrom(
      this._braintree.getClientToken(true)
    );

    const instance = await client.create({
      authorization: clientToken.token,
    });

    // The component may have been destroyed while the token request was open.
    if (this._destroyed) {
      await this._safeTeardown(instance);
      return;
    }

    this._client = instance;

    const [bank, collector] = await Promise.all([
      usBankAccount.create({ client: instance }),
      dataCollector.create({ client: instance }).catch((error: unknown) => {
        console.warn("eCheck device data collection is unavailable.", error);
        return null;
      }),
    ]);

    if (this._destroyed) {
      await Promise.all([
        this._safeTeardown(bank),
        this._safeTeardown(collector),
        this._safeTeardown(instance),
      ]);
      return;
    }

    this._usBankAccount = bank;
    this._dataCollector = collector;
  }

  /** Shuts down a Braintree SDK object if it is present and has teardown(). */
  private async _safeTeardown(instance: unknown): Promise<void> {
    if (!this._hasTeardown(instance)) {
      return;
    }

    try {
      await instance.teardown();
    } catch (error) {
      console.warn("Could not tear down a Braintree eCheck instance.", error);
    }
  }

  private _hasTeardown(
    instance: unknown
  ): instance is { teardown: () => unknown } {
    return (
      instance !== null &&
      typeof instance === "object" &&
      "teardown" in instance &&
      typeof instance.teardown === "function"
    );
  }
}
